import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from './components/Header';
import TabsNav from './components/TabsNav';
import OverviewSection from './components/OverviewSection';
import TasksSection from './components/TasksSection';
import CommentsSection from './components/CommentsSection';
import SettingsSection from './components/SettingsSection';
import AddTaskModal from './components/modals/AddTaskModal';
import EditTaskModal from './components/modals/EditTaskModal';
import EditProjectModal from './components/modals/EditProjectModal';
import EditCommentModal from './components/modals/EditCommentModal';
import { Project, Task, Comment, ProjectCredentials } from './types';

export default function ProjectDetails() {
  const { id: projectId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [credentials, setCredentials] = useState<Partial<ProjectCredentials>>({});
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'comments' | 'settings'>('overview');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);

  const [showEditCommentModal, setShowEditCommentModal] = useState(false);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');

  const clampPercent = (value: number) => Math.max(0, Math.min(100, Math.round(value)));
  const normalize = (s: string | null) => (s || '').toLowerCase();

  const calcProgressFromTasks = (list: Pick<Task, 'status'>[]) => {
    const total = list.length;
    if (total === 0) return 0;
    const completed = list.filter((t) => {
      const n = normalize(t.status as unknown as string);
      return n === 'concluída' || n === 'concluida' || n === 'concluído' || n === 'concluido';
    }).length;
    return clampPercent((completed / total) * 100);
  };

  const updateProjectProgress = async (currentProjectId: string) => {
    const { data: tasksDataRaw, error: tasksError } = await supabase
      .from('tasks')
      .select('status')
      .eq('project_id', currentProjectId);

    if (tasksError) {
      console.error("Error fetching tasks for progress update:", tasksError);
      return;
    }

    const tasksData = tasksDataRaw || [];
    const newProgress = calcProgressFromTasks(tasksData as any);

    const { error: updateError } = await supabase
      .from('projects')
      .update({ progress: newProgress })
      .eq('id', currentProjectId);

    if (updateError) {
      console.error("Error updating project progress:", updateError);
    } else {
      if (project?.id === currentProjectId) {
        setProject(prev => prev ? { ...prev, progress: newProgress } : null);
      }
    }
  };

  useEffect(() => {
    if (projectId && user) {
      fetchProjectData();
    } else if (!user) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user]);

  useEffect(() => {
    if (projectId && user) {
      updateProjectProgress(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.length]);

  const fetchProjectData = async () => {
    if (!projectId) return;
    setLoading(true);
    
    const { data: projectData, error: projectError } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .eq('user_id', user!.id)
      .single();

    if (projectError || !projectData) {
      console.error('Error fetching project:', projectError);
      setLoading(false);
      return;
    }
    setProject(projectData);

    const { data: tasksData, error: tasksError } = await supabase
      .from('tasks')
      .select('*')
      .eq('project_id', projectId);
    if (tasksError) console.error('Error fetching tasks:', tasksError);
    else setTasks(tasksData || []);

    const { data: commentsData, error: commentsError } = await supabase
      .from('comments')
      .select('*, profiles(full_name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (commentsError) console.error('Error fetching comments:', commentsError);
    else setComments(commentsData as any || []);

    const { data: credentialsData, error: credentialsError } = await supabase
      .from('project_configurations')
      .select('*')
      .eq('project_id', projectId)
      .single();
    if (credentialsError && credentialsError.code !== 'PGRST116') {
      console.error('Error fetching credentials:', credentialsError);
    } else {
      setCredentials(credentialsData || {});
    }
    
    await updateProjectProgress(projectId);

    setLoading(false);
  };

  // Handlers Tarefas
  const handleCreateTask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user || !projectId) return;

    const formData = new FormData(e.currentTarget);
    const newTask = {
      project_id: projectId,
      user_id: user.id,
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      status: formData.get('status') as Task['status'],
      priority: formData.get('priority') as Task['priority'],
      assignee: formData.get('assignee') as string,
      due_date: formData.get('due_date') as string,
    };

    const { data, error } = await supabase.from('tasks').insert(newTask).select().single();
    if (error) {
      alert('Erro ao criar tarefa.');
      console.error(error);
    } else if (data) {
      setTasks([data, ...tasks]);
      setShowTaskModal(false);
      await updateProjectProgress(projectId);
    }
  };

  const handleUpdateTask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingTask || !projectId) return;

    const formData = new FormData(e.currentTarget);
    const updatedTask = {
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      status: formData.get('status') as Task['status'],
      priority: formData.get('priority') as Task['priority'],
      assignee: formData.get('assignee') as string,
      due_date: formData.get('due_date') as string,
    };

    const { data, error } = await supabase.from('tasks').update(updatedTask).eq('id', editingTask.id).select().single();
    if (error) {
      alert('Erro ao atualizar tarefa.');
      console.error(error);
    } else if (data) {
      setTasks(tasks.map(t => t.id === data.id ? data : t));
      setShowEditTaskModal(false);
      setEditingTask(null);
      await updateProjectProgress(projectId);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta tarefa?') || !projectId) return;
    const { error } = await supabase.from('tasks').delete().eq('id', taskId);
    if (error) {
      alert('Erro ao excluir tarefa.');
      console.error(error);
    } else {
      setTasks(tasks.filter(t => t.id !== taskId));
      await updateProjectProgress(projectId);
    }
  };

  // Handlers Comentários
  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user || !projectId) return;

    const { data, error } = await supabase
      .from('comments')
      .insert({ content: newComment, project_id: projectId, user_id: user.id })
      .select('*')
      .single();

    if (error) {
      alert('Erro ao adicionar comentário.');
      console.error(error);
    } else if (data) {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', data.user_id)
        .single();

      const commentWithProfile = {
        ...data,
        profiles: profileError ? null : { full_name: profileData?.full_name ?? null },
      } as Comment;

      setComments([commentWithProfile, ...comments]);
      setNewComment('');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Tem certeza que deseja excluir este comentário?')) return;
    const { error } = await supabase.from('comments').delete().eq('id', commentId);
    if (error) {
      alert('Erro ao excluir comentário.');
      console.error(error);
    } else {
      setComments(comments.filter(c => c.id !== commentId));
    }
  };

  const openEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setEditingCommentContent(comment.content);
    setShowEditCommentModal(true);
  };

  const handleUpdateComment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingComment) return;

    const { data, error } = await supabase
      .from('comments')
      .update({ content: editingCommentContent })
      .eq('id', editingComment.id)
      .select('*')
      .single();

    if (error) {
      alert('Erro ao atualizar comentário.');
      console.error(error);
    } else if (data) {
      setComments(prev =>
        prev.map(c =>
          c.id === data.id
            ? { ...c, content: data.content }
            : c
        )
      );
      setShowEditCommentModal(false);
      setEditingComment(null);
      setEditingCommentContent('');
    }
  };

  // Handler Projeto
  const handleUpdateProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!project) return;

    const formData = new FormData(e.currentTarget);
    const updatedProject = {
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      status: formData.get('status') as string,
      priority: formData.get('priority') as string,
      start_date: formData.get('startDate') as string,
      end_date: formData.get('endDate') as string,
      responsible: formData.get('responsible') as string,
    };

    const { data, error } = await supabase.from('projects').update(updatedProject).eq('id', project.id).select().single();
    if (error) {
      alert('Erro ao atualizar projeto.');
      console.error(error);
    } else if (data) {
      setProject(data);
      setShowEditProjectModal(false);
    }
  };

  // Credenciais
  const handleSaveCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !user) return;

    const { error } = await supabase.from('project_configurations').upsert({
      ...credentials,
      project_id: projectId,
      id: (credentials as any).id
    });

    if (error) {
      alert('Erro ao salvar configurações.');
      console.error(error);
    } else {
      setIsEditingCredentials(false);
      alert('Configurações salvas com sucesso!');
    }
  };

  const handleCredentialChange = (field: keyof ProjectCredentials, value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
  };

  // UI Helpers
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo': return 'bg-green-100 text-green-800';
      case 'Concluída': return 'bg-blue-100 text-blue-800';
      case 'Em Andamento': return 'bg-yellow-100 text-yellow-800';
      case 'Pendente': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Alta': return 'bg-red-100 text-red-800';
      case 'Média': return 'bg-yellow-100 text-yellow-800';
      case 'Baixa': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }

  if (!project) {
    return <div className="min-h-screen flex items-center justify-center">Projeto não encontrado ou acesso negado.</div>;
  }

  // Progresso exibido: preferir cálculo local das tarefas; se não houver tarefas, usar o valor salvo no projeto
  const localProgress = calcProgressFromTasks(tasks);
  const displayProgress = tasks.length > 0 ? localProgress : clampPercent(Number(project.progress) || 0);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${project.title} - Detalhes do Projeto`,
    "description": `Detalhes completos do projeto ${project.title}. Gerencie tarefas, equipe e configurações.`,
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/${project.id}`,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={`${project.title} - Detalhes do Projeto - Vibecode`}
        description={`Detalhes completos do projeto ${project.title}. Gerencie tarefas, equipe e configurações.`}
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/project/${project.id}`}
        jsonLd={jsonLd}
      />

      <Header
        onGoDashboard={() => navigate('/dashboard')}
        userInitial={user?.email?.charAt(0).toUpperCase()}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.title}</h1>
              <p className="text-gray-600 mb-4">{project.description}</p>
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(project.priority)}`}>
                  Prioridade {project.priority}
                </span>
              </div>
            </div>
            <div className="mt-6 lg:mt-0 lg:ml-6">
              <div className="flex space-x-3">
                <button onClick={() => setShowEditProjectModal(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-edit-line mr-2"></i>
                  Editar Projeto
                </button>
              </div>
            </div>
          </div>

          <TabsNav
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tasksCount={tasks.length}
            commentsCount={comments.length}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          {activeTab === 'overview' && (
            <OverviewSection project={project} safeProgress={displayProgress} />
          )}

          {activeTab === 'tasks' && (
            <TasksSection
              tasks={tasks}
              onCreate={() => setShowTaskModal(true)}
              onEdit={(t) => { setEditingTask(t); setShowEditTaskModal(true); }}
              onDelete={handleDeleteTask}
              getStatusColor={getStatusColor}
              getPriorityColor={getPriorityColor}
            />
          )}

          {activeTab === 'comments' && (
            <CommentsSection
              comments={comments}
              newComment={newComment}
              onChangeNewComment={setNewComment}
              onSubmit={handleAddComment}
              currentUserId={user?.id}
              onEdit={openEditComment}
              onDelete={handleDeleteComment}
            />
          )}

          {activeTab === 'settings' && (
            <SettingsSection
              credentials={credentials}
              isEditing={isEditingCredentials}
              onToggleEdit={() => setIsEditingCredentials(!isEditingCredentials)}
              onSave={handleSaveCredentials}
              onChange={handleCredentialChange}
            />
          )}
        </div>
      </div>

      <AddTaskModal
        show={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onSubmit={handleCreateTask}
      />

      <EditTaskModal
        show={showEditTaskModal}
        task={editingTask}
        onClose={() => setShowEditTaskModal(false)}
        onSubmit={handleUpdateTask}
      />

      <EditProjectModal
        show={showEditProjectModal}
        project={project}
        onClose={() => setShowEditProjectModal(false)}
        onSubmit={handleUpdateProject}
      />

      <EditCommentModal
        show={showEditCommentModal && !!editingComment}
        value={editingCommentContent}
        onChange={setEditingCommentContent}
        onClose={() => { setShowEditCommentModal(false); setEditingComment(null); setEditingCommentContent(''); }}
        onSubmit={handleUpdateComment}
      />
    </div>
  );
}